import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/Reveal";
import { scrollToId } from "../lib/scroll";
import { useNavigate } from "react-router-dom";

const content = {
  en: {
    overline: "Case Study",
    title: "A Riyadh dental clinic, 30 days on Sawtly",
    sub: "Anonymized results from a real deployment. Numbers rounded for privacy.",
    stats: [
      { k: "68%", v: "of missed calls converted to bookings" },
      { k: "212", v: "after-hours calls answered in month one" },
      { k: "94,000 SAR", v: "recovered revenue in 30 days" },
      { k: "<1s", v: "average answer time, first ring" },
    ],
    story: [
      { h: "The situation", p: "A three-chair dental clinic in north Riyadh with one receptionist. Front-desk data showed 28–35 unanswered calls a day, concentrated between 12–2 PM and after 9 PM. The owner assumed most were spam. They weren't." },
      { h: "The deployment", p: "Sawtly was configured in four days: Najdi dialect, service menu with pricing, calendar integration with 15-minute buffers, and an emergency transfer rule to the owner's mobile. The clinic changed nothing about how it operated." },
      { h: "The first surprise", p: "The very first weekend, the agent booked 31 appointments — 24 of them after 8 PM. The receptionist, freed from the phone, started doing insurance follow-ups that had been backlogged for months." },
      { h: "The owner's take", p: "\"I thought I had a marketing problem. I had an answering problem. We were paying for ads to make the phone ring, then not picking it up.\"" },
    ],
    cta: "Get results like this",
  },
  ar: {
    overline: "دراسة حالة",
    title: "عيادة أسنان في الرياض، 30 يوم مع صوتلي",
    sub: "نتائج حقيقية من نشر فعلي مع إخفاء الهوية. الأرقام مقربة للخصوصية.",
    stats: [
      { k: "68%", v: "من المكالمات الفائتة تحولت لحجوزات" },
      { k: "212", v: "مكالمة بعد الدوام تم الرد عليها في الشهر الأول" },
      { k: "94,000 ريال", v: "إيراد مسترجع في 30 يوم" },
      { k: "أقل من ثانية", v: "متوسط زمن الرد، من أول رنة" },
    ],
    story: [
      { h: "الوضع", p: "عيادة أسنان بثلاثة كراسي في شمال الرياض مع موظفة استقبال واحدة. بيانات الكاونتر بينت 28–35 مكالمة بدون رد يوميًا، متركزة بين 12–2 الظهر وبعد 9 بالليل. المالك كان يفترض إن أغلبها إعلانات مزعجة. ما كانت كذا." },
      { h: "النشر", p: "صوتلي اتضبط في أربعة أيام: لهجة نجدية، قائمة خدمات بالأسعار، تكامل مع التقويم بفواصل 15 دقيقة، وقاعدة تحويل طوارئ لجوال المالك. العيادة ما غيرت أي شي في طريقة شغلها." },
      { h: "أول مفاجأة", p: "أول نهاية أسبوع، الوكيل حجز 31 موعد — 24 منهم بعد الساعة 8 بالليل. موظفة الاستقبال، اللي تفرغت من الجوال، بدأت تسوي متابعات التأمين المتراكمة من شهور." },
      { h: "كلام المالك", p: "«كنت أفكر عندي مشكلة تسويق. طلع عندي مشكلة رد. كنا ندفع للإعلانات عشان الجوال يرن، وبعدين ما نرد عليه.»" },
    ],
    cta: "احصل على نتائج مثل هذه",
  },
};

const CaseStudy = () => {
  const { lang } = useLanguage();
  const c = content[lang];
  const navigate = useNavigate();

  return (
    <main data-testid="case-study-page" className="mx-auto max-w-7xl px-6 pb-24 pt-36 lg:px-10 lg:pt-44">
      <Reveal>
        <p className="overline-tag">{c.overline}</p>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-tight md:text-6xl">{c.title}</h1>
        <p className="mt-4 max-w-xl text-sm text-zinc-400 md:text-base">{c.sub}</p>
      </Reveal>

      <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {c.stats.map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div data-testid={`case-stat-${i}`} className="rounded-3xl border border-white/10 bg-surface p-8 text-start transition-colors duration-500 hover:border-gold/40">
              <p className="font-display text-3xl font-extrabold gold-text md:text-4xl">{s.k}</p>
              <p className="mt-3 text-sm text-zinc-500">{s.v}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-16 grid gap-10 lg:grid-cols-2">
        {c.story.map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="border-s-2 border-neon/40 ps-8" data-testid={`case-story-${i}`}>
              <h2 className="font-display text-xl font-bold md:text-2xl">{s.h}</h2>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400 md:text-base">{s.p}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <button onClick={() => { navigate("/"); setTimeout(() => scrollToId("deploy"), 350); }} data-testid="case-deploy-cta" className="btn-gold mt-16">
          {c.cta}
        </button>
      </Reveal>
    </main>
  );
};

export default CaseStudy;
